// SystemPill organism — extracted from MessageBubble.
//
// Renders system-level events (session init, turn boundaries, exits,
// config changes, stops, crashes, respawns) as centered pill badges.

import type { Message } from '../../types';
import { asSystem } from '../../lib/messages';
import { useTranslation } from '../../i18n';

export interface SystemPillProps {
  message: Message;
}

// Sprint stdout-transport-replay (D013-EC-118) — subtypes that signal
// LOST OR INCOMPLETE agent output. These must never render as calm
// muted pills: the founder has to notice them (spec D2 "loud, not
// silent"). Same status-error token pattern as AttachmentChip /
// ErrorBoundary (design-discipline: tokens only).
const LOUD_SUBTYPES = new Set(['transport_gap', 'output_truncated']);

export function SystemPill({ message }: SystemPillProps) {
  const { t } = useTranslation();
  const c = asSystem(message);
  const label = describeSystemMessage(c, t);
  if (!label) return null;
  const loud = typeof c.subtype === 'string' && LOUD_SUBTYPES.has(c.subtype);
  return (
    <div className="fade-in-0 slide-in-from-top-2 animate-in flex justify-center">
      <span
        className={
          loud
            ? 'text-caption px-2 py-1 rounded-full border border-status-error/40 bg-status-error/10 text-status-error'
            : 'text-caption px-2 py-1 rounded-full bg-card text-muted-foreground border border-border'
        }
        data-testid="system-pill"
        data-subtype={c.subtype}
      >
        {label}
      </span>
    </div>
  );
}

function describeSystemMessage(
  c: ReturnType<typeof asSystem>,
  t: (key: import('../../i18n').TKey, params?: Record<string, string | number>) => string,
): string | null {
  switch (c.subtype) {
    case 'init':
      return t('system.agentStarted');
    case 'turn_boundary':
      return t('system.newTurn');
    case 'result': {
      if ('duration_ms' in c && typeof c.duration_ms === 'number') {
        return t('system.turnCompleteWithDuration', {
          duration: `${(c.duration_ms / 1000).toFixed(1)}s`,
        });
      }
      return t('system.turnComplete');
    }
    case 'exit': {
      const status =
        'status' in c && typeof c.status === 'string' ? c.status : 'exited';
      return t('system.agentExited', { status });
    }
    case 'config_changed': {
      const to =
        'to' in c && typeof c.to === 'object' && c.to !== null
          ? (c.to as { model?: string; effort?: string })
          : null;
      if (to && to.model && to.effort) {
        return t('system.switchedTo', {
          model: shortenModel(to.model),
          effort: to.effort,
        });
      }
      return t('system.configChanged');
    }
    case 'stopped_by_user':
      return t('system.stopped');
    case 'crash':
      return t('system.agentCrashed');
    case 'respawning':
      return t('system.reopenedConversation');
    case 'respawned':
      return t('system.readyContinuing');
    // Sprint stdout-transport-replay (D013-EC-118) — visible loss
    // surfaces. Deliberately NOT in ConversationList's
    // HIDDEN_SYSTEM_SUBTYPES: the founder must see them.
    case 'transport_gap':
      return t('system.outputLost');
    case 'output_truncated':
      return t('system.outputTruncated');
    default:
      // Sprint messages-section-fixes (D-msg-11) — unknown / future
      // subtypes are developer-shaped jargon (e.g. 'consolidated',
      // 'reconciled-2'). Pre-fix, the raw subtype string was rendered
      // verbatim, leaking technical names to the founder. Returning
      // `null` causes the calling component to short-circuit. If a new
      // subtype deserves a founder-readable label, add an explicit
      // case here in the same PR that introduces the subtype.
      return null;
  }
}

function shortenModel(model: string): string {
  const m = model.match(/^claude-([a-z]+)-(\d+)-(\d+)/);
  if (m) return `${m[1]} ${m[2]}.${m[3]}`;
  return model;
}
